import * as THREE from 'three';
import type { BoardNode } from '../../../shared/board/types';
import type { MapHandle } from '../maps';
import type {
  BoardObjectGltfLoader,
  BoardOverlayAssetKind,
  BoardObjectOverlay,
} from './chest-overlay';
import type { GsapRegistry } from '../vfx/gsap-registry';
import { getWorldPositionForNode, type SurfaceLookup } from './surface-y';

const CACHE_MODEL_URL = '/models/weapons-cache.glb';
const SURFACE_OFFSET = 0.04;
const TARGET_MAX_DIMENSION = 0.76;
const RISE_DEPTH = 2;
const RISE_DURATION_SECONDS = 0.6;

function findNode(nodeId: string, mapHandle: MapHandle): BoardNode | undefined {
  return (mapHandle.schema.nodes as readonly BoardNode[]).find((node) => node.id === nodeId);
}

function disposeObject(root: THREE.Object3D): void {
  root.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh)) return;
    obj.geometry?.dispose();
    const material = obj.material;
    if (Array.isArray(material)) {
      for (const mat of material) mat.dispose();
    } else {
      material.dispose();
    }
  });
}

function normalizeToTileFootprint(root: THREE.Object3D): void {
  root.updateWorldMatrix(true, true);
  const box = new THREE.Box3().setFromObject(root);
  const size = box.getSize(new THREE.Vector3());
  const maxDimension = Math.max(size.x, size.y, size.z);
  if (maxDimension > 0) {
    root.scale.multiplyScalar(TARGET_MAX_DIMENSION / maxDimension);
    root.updateWorldMatrix(true, true);
    box.setFromObject(root);
  }

  const center = box.getCenter(new THREE.Vector3());
  root.position.x -= center.x;
  root.position.y -= box.min.y;
  root.position.z -= center.z;
}

function buildProceduralCache(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'weapons-cache-procedural';

  const crateMat = new THREE.MeshStandardMaterial({
    color: 0x42484f,
    roughness: 0.58,
    metalness: 0.12,
  });
  const rimMat = new THREE.MeshStandardMaterial({
    color: 0x91a4aa,
    emissive: 0x28d8ff,
    emissiveIntensity: 0.32,
    metalness: 0.7,
    roughness: 0.22,
  });

  const body = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.34, 0.4), crateMat);
  body.position.y = 0.17;
  group.add(body);

  const topRim = new THREE.Mesh(new THREE.BoxGeometry(0.61, 0.05, 0.46), rimMat);
  topRim.position.y = 0.37;
  group.add(topRim);

  const frontRim = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.08, 0.04), rimMat);
  frontRim.position.set(0, 0.2, 0.23);
  group.add(frontRim);

  const sideRim = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.38, 0.46), rimMat);
  sideRim.position.set(-0.31, 0.19, 0);
  group.add(sideRim);

  const sideRimMirror = sideRim.clone();
  sideRimMirror.position.x = 0.31;
  group.add(sideRimMirror);

  return group;
}

export function createWeaponsCacheOverlay(args: {
  parent: THREE.Group;
  surfaceLookup: SurfaceLookup;
  gltfLoader?: BoardObjectGltfLoader | null;
  gsapRegistry?: GsapRegistry | null;
}): BoardObjectOverlay {
  const group = new THREE.Group();
  group.name = 'weapons-cache-overlay';
  group.visible = false;
  args.parent.add(group);

  let disposed = false;
  let currentNodeId: string | null = null;
  let currentMapHandle: MapHandle | null = null;
  let visual: THREE.Object3D | null = buildProceduralCache();
  let assetKind: BoardOverlayAssetKind = args.gltfLoader == null ? 'procedural' : 'loading';
  let firstAppearanceStarted = false;
  let riseHandle: ReturnType<GsapRegistry['create']> | null = null;
  group.add(visual);

  function resolveCurrentPlacement(): { position: THREE.Vector3; finalY: number } | null {
    if (currentNodeId == null || currentMapHandle == null) {
      group.visible = false;
      return null;
    }

    const node = findNode(currentNodeId, currentMapHandle);
    if (node == null) {
      group.visible = false;
      return null;
    }

    const position = getWorldPositionForNode(node, args.surfaceLookup);
    return { position, finalY: position.y + SURFACE_OFFSET };
  }

  function placeCurrent(): void {
    const placement = resolveCurrentPlacement();
    if (placement == null) return;

    const { position, finalY } = placement;
    group.position.set(position.x, finalY, position.z);
    group.visible = true;
  }

  function replaceVisual(nextVisual: THREE.Object3D, nextKind: BoardOverlayAssetKind): void {
    if (visual != null) {
      group.remove(visual);
      disposeObject(visual);
    }
    visual = nextVisual;
    assetKind = nextKind;
    normalizeToTileFootprint(visual);
    group.add(visual);
    if (riseHandle == null) {
      placeCurrent();
    }
  }

  function stopRise(): void {
    if (riseHandle == null) return;
    riseHandle.dispose();
    riseHandle = null;
  }

  function playRiseFromUnderground(force = false): Promise<void> {
    const placement = resolveCurrentPlacement();
    if (placement == null) return Promise.resolve();
    if ((!force && firstAppearanceStarted) || args.gsapRegistry == null) {
      placeCurrent();
      return Promise.resolve();
    }

    firstAppearanceStarted = true;
    stopRise();

    const { position, finalY } = placement;
    group.position.set(position.x, finalY - RISE_DEPTH, position.z);
    group.visible = true;

    return new Promise((resolve) => {
      const handle = args.gsapRegistry!.create('weapons-cache:first-rise');
      riseHandle = handle;
      handle.timeline.to(
        group.position,
        {
          y: finalY,
          duration: RISE_DURATION_SECONDS,
          ease: 'power3.out',
        },
        0,
      );
      handle.timeline.call(
        () => {
          if (riseHandle === handle) {
            riseHandle = null;
          }
          handle.dispose();
          group.position.set(position.x, finalY, position.z);
          resolve();
        },
        undefined,
        RISE_DURATION_SECONDS,
      );
    });
  }

  if (args.gltfLoader != null) {
    void args.gltfLoader
      .load(CACHE_MODEL_URL)
      .then((scene) => {
        if (disposed) {
          disposeObject(scene);
          return;
        }
        scene.name = 'weapons-cache-glb';
        replaceVisual(scene, 'glb');
      })
      .catch((err) => {
        console.warn('[smashparty] weapons cache GLB failed; using procedural fallback:', err);
        assetKind = 'procedural';
        placeCurrent();
      });
  }

  return {
    group,
    get assetKind(): BoardOverlayAssetKind {
      return assetKind;
    },
    update(activeNodeId: string | null, mapHandle: MapHandle): void {
      currentMapHandle = mapHandle;
      if (activeNodeId === currentNodeId) return;
      stopRise();
      currentNodeId = activeNodeId;
      placeCurrent();
    },
    playFirstAppearance(activeNodeId: string | null, mapHandle: MapHandle): Promise<void> {
      currentMapHandle = mapHandle;
      if (activeNodeId == null || activeNodeId === '') {
        stopRise();
        currentNodeId = activeNodeId;
        placeCurrent();
        return Promise.resolve();
      }
      const wasHidden = currentNodeId == null || currentNodeId === '';
      currentNodeId = activeNodeId;
      if (!wasHidden) {
        placeCurrent();
        return Promise.resolve();
      }
      return playRiseFromUnderground();
    },
    playRelocationAppearance(activeNodeId: string | null, mapHandle: MapHandle): Promise<void> {
      currentMapHandle = mapHandle;
      if (activeNodeId == null || activeNodeId === '') {
        stopRise();
        currentNodeId = activeNodeId;
        placeCurrent();
        return Promise.resolve();
      }
      currentNodeId = activeNodeId;
      return playRiseFromUnderground(true);
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      stopRise();
      args.parent.remove(group);
      if (visual != null) {
        disposeObject(visual);
        visual = null;
      }
      group.clear();
    },
  };
}
